import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ColumnType } from "antd/es/table";

import { PRACTICE_MANAGEMENT_ENDPOINT } from "services";

import { hasAdminTierEqualOrAbove, useAdminUserContext } from "utils";

import { PRACTICE_MANAGEMENT_COUNTRY_SELECT_OPTIONS } from "consts";

import { Button, Flex, Form, IconButton, Input, InputNumber, InputSearch, Modal, Option, Select, Spin, Switch, Table } from "components";

import { CompareVersionsButton } from "pages/admin/practiceManagementService/CompareVersionsButton";
import { PublishButton } from "pages/admin/practiceManagementService/PublishButton";
import { ResetDraftButton } from "pages/admin/practiceManagementService/ResetDraftButton";
import { ResetToGlobalButton } from "pages/admin/practiceManagementService/ResetToGlobalButton";

/**
 * A row as the table sees it: an arbitrary PMS record plus the `editingKey`
 * which is used on the FE only to track which row is being edited
 */
type PracticeManagementRow = { editingKey: string } & Record<string, unknown>;

type PracticeManagementTableProps<T extends PracticeManagementRow> = {
  data: T[];
  loading?: boolean;
  onSaveDraft: (data: PracticeManagementRow[], idsToDelete: { id: string; country: PracticeManagement.CountryCode }[]) => void;
  /** The domain-specific identifier field (e.g. "cropId", "fertilizerId"). Omit for resources with no per-record identifier. */
  identifierField?: string;
  versions: string[];
  selectedVersion: string | undefined;
  isDraftVersion: boolean;
  hasDraftVersion: boolean;
  onVersionChange?: (v: string) => void;
  selectedCountry: PracticeManagement.CountryCode;
  onCountryChange: (c: PracticeManagement.CountryCode) => void;
  onPublish: React.ComponentProps<typeof PublishButton>["onPublish"];
  onResetDraft: () => Promise<void>;
  onCopyFromGlobal?: () => Promise<void>;
  onResetToGlobal: (endpoint: PRACTICE_MANAGEMENT_ENDPOINT) => Promise<void>;
  /** The practice-management endpoint backing this table, used to scope a reset to global. */
  endpoint: PRACTICE_MANAGEMENT_ENDPOINT;
};

const EXCLUDED_COLUMNS = new Set(["expandable", "key", "className"]);

type InputType = "number" | "boolean" | "text" | "number-array";

type EditableCellProps<T> = {
  editing: boolean;
  dataIndex: string;
  inputType: InputType;
  record: T;
} & React.TdHTMLAttributes<HTMLTableCellElement>;

const NumberArrayInput = ({ value, onChange, dataTestId }: { value?: number[]; onChange?: (v: number[]) => void; dataTestId?: string }) => (
  <Flex gap={8}>
    {(value ?? []).map((v, i) => (
      <div key={i} className="pms-editable-cell-array-input">
        <InputNumber
          value={v}
          label=""
          dataTestId={dataTestId ? `${dataTestId}-${i}` : undefined}
          onChange={(newVal) => {
            const next = [...(value ?? [])];
            next[i] = newVal as number;
            onChange?.(next);
          }}
        />
      </div>
    ))}
  </Flex>
);

const EditableCell = <T extends Record<string, unknown>>({
  editing,
  dataIndex,
  inputType,
  children,
  ...restProps
}: React.PropsWithChildren<EditableCellProps<T>>) => (
  <td {...restProps} className={`${restProps.className ?? ""} ${editing ? "" : "pms-editable-cell-viewing"}`}>
    {editing ? (
      <Form.Item name={dataIndex} className="pms-editable-cell-form-item" valuePropName={inputType === "boolean" ? "checked" : "value"}>
        {inputType === "number" ? (
          <InputNumber className="pms-editable-cell-input-number" label="" dataTestId={`pms-cell-${dataIndex}`} decimalSeparator="." />
        ) : inputType === "boolean" ? (
          <Switch />
        ) : inputType === "number-array" ? (
          <NumberArrayInput dataTestId={`pms-cell-${dataIndex}`} />
        ) : (
          <Input label="" className="pms-editable-cell-input" dataTestId={`pms-cell-${dataIndex}`} />
        )}
      </Form.Item>
    ) : (
      children
    )}
  </td>
);

export const buildEmptyRow = <T extends PracticeManagementRow>(template: T, country: PracticeManagement.CountryCode): T => {
  const row: Record<string, unknown> = { editingKey: `new-${Date.now()}` };
  for (const [k, v] of Object.entries(template)) {
    if (k !== "editingKey") {
      if (typeof v === "number") row[k] = 0;
      else if (typeof v === "boolean") row[k] = false;
      else if (Array.isArray(v) && v.every((item) => typeof item === "number")) row[k] = v.map(() => 0);
      else if (Array.isArray(v)) row[k] = [];
      else if (v !== null && typeof v === "object") row[k] = {};
      else row[k] = "";
    }
  }
  // Stamp the currently selected country so the new row is created for that country,
  // not the country of the template row (which is `global` when viewing a fallback table).
  row.country = country;
  return row as T;
};

// Both helpers read all rows, as a row may lack a field or hold `null` for it

/** Every field across all rows, in first-seen order */
export const getColumnKeys = (rows: PracticeManagementRow[]): string[] => [...new Set(rows.flatMap((row) => Object.keys(row)))];

/** The first non-null value of a column, used to pick its input type */
export const getColumnSample = (rows: PracticeManagementRow[], column: string): unknown =>
  rows.find((row) => row[column] !== null && row[column] !== undefined)?.[column];

export const PracticeManagementTable = <T extends PracticeManagementRow>({
  data,
  loading,
  onSaveDraft,
  identifierField,
  versions,
  selectedVersion,
  isDraftVersion,
  hasDraftVersion,
  onVersionChange,
  selectedCountry,
  onCountryChange,
  onPublish,
  onResetDraft,
  onCopyFromGlobal,
  onResetToGlobal,
  endpoint,
}: PracticeManagementTableProps<T>) => {
  const [form] = Form.useForm();
  const [tableData, setTableData] = useState<T[]>([]);
  const [editingKey, setEditingKey] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pendingScrollKey, setPendingScrollKey] = useState<string | null>(null);
  const [pendingDeleteRecord, setPendingDeleteRecord] = useState<T | null>(null);
  const [isCopyingFromGlobal, setIsCopyingFromGlobal] = useState(false);

  const { user } = useAdminUserContext();
  const canEdit = hasAdminTierEqualOrAbove(user?.role, "tier2");

  useEffect(() => {
    setTableData(data ?? []);
  }, [data]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const query = searchQuery.toLowerCase();
    return tableData.filter((row) =>
      Object.entries(row).some(([col, value]) => {
        if (EXCLUDED_COLUMNS.has(col)) return false;
        if (Array.isArray(value) || (value !== null && typeof value === "object")) return false;
        return String(value ?? "")
          .toLowerCase()
          .includes(query);
      })
    );
  }, [tableData, searchQuery]);

  const isGlobalDraftFallback =
    selectedVersion === "draft" && selectedCountry !== "global" && tableData.length > 0 && tableData.every((row) => row.country === "global");

  const isEditing = useCallback((record: T) => editingKey && record?.editingKey === editingKey, [editingKey]);

  const edit = useCallback(
    (record: T) => {
      form.setFieldsValue(record);
      setEditingKey(record.editingKey);
    },
    [form]
  );

  const cancel = useCallback(() => setEditingKey(undefined), []);

  const save = useCallback(
    async (key: string) => {
      const values = (await form.validateFields()) as Partial<T>;
      const newData = tableData.map((item) => (item.editingKey === key ? { ...item, ...values } : item));
      setTableData(newData);
      setEditingKey(undefined);
      const savedRow = newData.find((item) => item.editingKey === key)!;
      onSaveDraft([savedRow], []);
    },
    [form, tableData, onSaveDraft]
  );

  const columns = useMemo((): ColumnType<T>[] => {
    if (!tableData.length) return [];

    const PINNED_RIGHT_COLUMNS = ["country", "version"];
    const NAMED_COLUMN_WIDTHS: Record<string, number> = { country: 96, version: 180 };

    const allDataCols: ColumnType<T>[] = getColumnKeys(tableData)
      .filter((col) => !EXCLUDED_COLUMNS.has(col))
      .map((col) => {
        const isEditingKeyCol = col === "editingKey";
        const sample = getColumnSample(tableData, col);
        const isNumberArray = Array.isArray(sample) && (sample as unknown[]).every((v) => typeof v === "number");
        const isNewRowOnlyField = col === identifierField || col === "country";
        const isEditableCol =
          col !== "name" && col !== "version" && (isNumberArray || (!Array.isArray(sample) && !(sample !== null && typeof sample === "object")));
        const inputType: InputType = isNumberArray ? "number-array" : typeof sample === "number" ? "number" : typeof sample === "boolean" ? "boolean" : "text";

        const colWidth =
          NAMED_COLUMN_WIDTHS[col] ??
          (inputType === "boolean"
            ? 80
            : inputType === "number"
            ? 96
            : inputType === "number-array"
            ? Math.max(96, (sample as number[]).length * 96)
            : undefined);

        return {
          title: col,
          dataIndex: col,
          key: col,
          hidden: isEditingKeyCol,
          width: colWidth,
          className: "paragraph-xs-bold",
          sorter: isEditableCol
            ? (a: T, b: T) => {
                const av = a[col];
                const bv = b[col];
                if (typeof av === "number" && typeof bv === "number") return av - bv;
                if (typeof av === "boolean" && typeof bv === "boolean") return Number(av) - Number(bv);
                return String(av ?? "").localeCompare(String(bv ?? ""));
              }
            : undefined,
          onCell: isEditableCol
            ? (record: T) =>
                ({
                  record,
                  inputType,
                  dataIndex: col,
                  editing: isEditing(record) && (!isNewRowOnlyField || record.editingKey?.startsWith("new-")),
                }) as React.TdHTMLAttributes<HTMLTableCellElement>
            : undefined,
          render: (value: unknown) => {
            // ":"-separated because "," reads as a decimal separator for European colleagues
            if (Array.isArray(value)) return value.join(" : ");
            if (value !== null && typeof value === "object") return JSON.stringify(value);
            if (typeof value === "boolean") return <Switch checked={value} disabled />;
            return String(value ?? "");
          },
        };
      });

    const dataCols = allDataCols.filter((col) => !PINNED_RIGHT_COLUMNS.includes(col.key as string));
    const pinnedCols = PINNED_RIGHT_COLUMNS.flatMap((key) => allDataCols.filter((col) => col.key === key));

    const actionsCol: ColumnType<T> = {
      title: "actions",
      key: "actions",
      render: (_: unknown, record: T) =>
        isEditing(record) ? (
          <Flex gap={16}>
            <IconButton onClick={() => save(record.editingKey)} iconName="tabler/check" tooltip={{ title: "Save" }} size="small" />
            <IconButton onClick={cancel} iconName="tabler/x" tooltip={{ title: "Cancel" }} size="small" />
          </Flex>
        ) : (
          <Flex gap={16}>
            <IconButton
              disabled={!!editingKey || !isDraftVersion || !canEdit}
              onClick={() => edit(record)}
              iconName="tabler/pencil"
              tooltip={{ title: "Edit row" }}
              size="small"
            />
            <IconButton
              disabled={!!editingKey || !isDraftVersion || !identifierField || !canEdit}
              onClick={() => setPendingDeleteRecord(record)}
              iconName="tabler/trash"
              tooltip={{ title: identifierField ? "Delete row" : "This resource has no per-record identifier and cannot be deleted" }}
              size="small"
            />
          </Flex>
        ),
    };

    return [...dataCols, ...pinnedCols, actionsCol];
  }, [tableData, editingKey, isEditing, edit, cancel, save, setPendingDeleteRecord, isDraftVersion, identifierField, canEdit]);

  useEffect(() => {
    if (!pendingScrollKey) return;
    const el = document.querySelector(`[data-row-key="${pendingScrollKey}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    setPendingScrollKey(null);
  }, [pendingScrollKey]);

  const handleAddRow = () => {
    if (!tableData.length) return;
    const newRow = buildEmptyRow(tableData[0], selectedCountry);
    const newTotal = tableData.length + 1;
    setTableData((prev) => [...prev, newRow]);
    setCurrentPage(Math.ceil(newTotal / pageSize));
    setPendingScrollKey(newRow.editingKey);
  };

  const handleCopyFromGlobal = async () => {
    if (!onCopyFromGlobal) return;
    setIsCopyingFromGlobal(true);
    try {
      await onCopyFromGlobal();
    } finally {
      setIsCopyingFromGlobal(false);
    }
  };

  const confirmDelete = () => {
    if (!pendingDeleteRecord) return;
    const key = pendingDeleteRecord.editingKey;
    const newData = tableData.filter((item) => item.editingKey !== key);
    const recordId = identifierField ? (pendingDeleteRecord[identifierField] as string) : undefined;
    setTableData(newData);
    setPendingDeleteRecord(null);
    if (!recordId || key.startsWith("new-")) return;
    onSaveDraft([], [{ id: recordId, country: selectedCountry }]);
  };

  // "Parameters" are unversioned => we don't render the version-specific UI
  // elements / buttons / etc.
  const isVersioned = !!onVersionChange;

  if (loading || !selectedVersion)
    return (
      <Flex justify="center" align="center" className="pms-spinner-container">
        <Spin size="large" />
      </Flex>
    );

  return (
    <Form form={form} component={false}>
      <Flex className="margin-top-100 margin-bottom-100" justify="space-between">
        <InputSearch className="pms-search" label="Search..." onChange={(e) => setSearchQuery(e.target.value)} dataTestId="pms-search" />
        <Flex gap={16} align="center">
          <IconButton
            type="tertiary"
            disabled={!isDraftVersion || !canEdit}
            onClick={handleAddRow}
            iconName="klim/plus"
            tooltip={{ title: "Add row" }}
            size="middle"
          />
          <Select
            value={selectedCountry}
            initialValue={selectedCountry}
            onChange={onCountryChange}
            label="Country"
            className="pms-country-select"
            dataTestId="pms-country-select"
          >
            {PRACTICE_MANAGEMENT_COUNTRY_SELECT_OPTIONS.map((c) => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Select>
          {isVersioned && (
            <Select
              value={selectedVersion}
              initialValue={selectedVersion}
              onChange={onVersionChange}
              label="Version"
              className="pms-version-select"
              dataTestId="pms-version-select"
            >
              {versions.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Select>
          )}
          {isVersioned && <CompareVersionsButton versions={versions} className="pms-header-button" />}
          <ResetDraftButton
            onResetDraft={onResetDraft}
            isDraftVersion={isDraftVersion}
            hasDraftVersion={hasDraftVersion}
            canEdit={canEdit}
            className="pms-header-button"
          />
          <ResetToGlobalButton
            onResetToGlobal={() => onResetToGlobal(endpoint)}
            isDraftVersion={isDraftVersion}
            selectedCountry={selectedCountry}
            canEdit={canEdit}
            className="pms-header-button"
          />
          {isVersioned && (
            <PublishButton
              onPublish={onPublish}
              isDraftVersion={isDraftVersion}
              selectedVersion={selectedVersion}
              versions={versions}
              canEdit={canEdit}
              className="pms-header-button"
            />
          )}
        </Flex>
      </Flex>
      <Table
        data={isGlobalDraftFallback ? [] : filteredData}
        loading={loading}
        columns={columns}
        rowKey="editingKey"
        className="pms-table"
        scroll={{ x: "max-content" }}
        components={{
          body: { cell: EditableCell },
        }}
        rowClassName={() => "editable-row"}
        pagination={{
          current: currentPage,
          pageSize,
          pageSizeOptions: [10, 20, 50],
          showTotal: (total) => `${total} rows`,
          hideOnSinglePage: false,
          onChange: (page, size) => {
            cancel();
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />
      {(tableData.length === 0 || isGlobalDraftFallback) && !loading && selectedCountry !== "global" && isDraftVersion && canEdit && (
        <Flex justify="center" className="margin-top-200">
          <Button type="secondary" loading={isCopyingFromGlobal} onClick={handleCopyFromGlobal}>
            Copy from global
          </Button>
        </Flex>
      )}
      <Modal
        title="Delete row"
        description="Are you sure you want to delete this row?"
        showModal={!!pendingDeleteRecord}
        okText="Delete"
        okButtonProps={{ danger: true }}
        onOk={confirmDelete}
        onCancel={() => setPendingDeleteRecord(null)}
      />
    </Form>
  );
};
